import ApexCharts from "apexcharts";

// Debounce function to prevent rapid chart recreation
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Safe chart destroyer with error handling
export const safeDestroyChart = (chartRef: React.MutableRefObject<ApexCharts | null>, chartName: string = "chart") => {
  if (chartRef.current) {
    try {
      chartRef.current.destroy();
    } catch (error) {
      // Silently handle destruction errors as they're often harmless
      console.debug(`Chart destruction ${chartName}:`, error);
    } finally {
      chartRef.current = null;
    }
  }
};

// Safe chart creator with ResizeObserver error suppression
export const safeCreateChart = async (
  containerRef: React.MutableRefObject<HTMLDivElement | null>,
  chartRef: React.MutableRefObject<ApexCharts | null>,
  options: any,
  chartName: string = "chart"
): Promise<boolean> => {
  if (!containerRef.current) {
    console.debug(`Container not ready for ${chartName}`);
    return false;
  }

  try {
    // Destroy existing chart first
    safeDestroyChart(chartRef, chartName);

    // Create new chart with a small delay to avoid ResizeObserver conflicts
    await new Promise(resolve => setTimeout(resolve, 10));
    
    chartRef.current = new ApexCharts(containerRef.current, options);
    
    // Render with error suppression
    await chartRef.current.render();
    
    return true;
  } catch (error) {
    console.warn(`Error creating ${chartName}:`, error);
    chartRef.current = null;
    return false;
  }
};

// Chart update manager with debouncing
export class ChartManager {
  private static instances = new Map<string, {
    updateFunction: Function;
    debounced: Function;
  }>();

  static registerChart(id: string, updateFunction: Function, debounceMs: number = 100) {
    const debounced = debounce(updateFunction, debounceMs);
    this.instances.set(id, { updateFunction, debounced });
    return debounced;
  }

  static updateChart(id: string, ...args: any[]) {
    const instance = this.instances.get(id);
    if (instance) {
      instance.debounced(...args);
    }
  }

  static unregisterChart(id: string) {
    this.instances.delete(id);
  }

  static cleanup() {
    this.instances.clear();
  }
}

// Global ResizeObserver error suppression specifically for ApexCharts
export const suppressResizeObserverErrors = () => {
  const originalError = console.error;
  console.error = (...args: any[]) => {
    // Suppress specific ApexCharts ResizeObserver errors
    if (
      args[0] && 
      typeof args[0] === 'string' && 
      args[0].includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      return;
    }
    originalError.apply(console, args);
  };
}; 